import { FC, FormEvent, useContext, useState } from 'react';
import styled from 'styled-components';
import { Stack, TextField, Typography } from '@mui/material';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useYupValidationResolver } from '@vs-online-only-app/ui';
import {
  Associate,
  AuthenticationContext,
  useUpdateAssociate,
} from '@vs-online-only-app/state';
import { ProfileSetupContext } from '../ProfileSetupContext';
import ProfileSetupError from '../ProfileSetupError';
import ProfileSetupContinueButton from '../ProfileSetupContinueButton';
import ProfileSetupBackButton from '../ProfileSetupBackButton';

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const validationSchema = yup.object({
  firstName: yup.string().required('Required'),
  lastName: yup.string().required('Required'),
});

export type FullNameFormFieldValues = {
  firstName: string;
  lastName: string;
};

const getDefaultFirstName = (user?: Associate) => {
  if (!user?.firstName) return '';
  if (user.firstName === 'Associate') return '';
  return user.firstName;
};

const EnterFullName: FC = () => {
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const { user, refetchUser } = useContext(AuthenticationContext);
  const { goToNextStep } = useContext(ProfileSetupContext);
  const resolver = useYupValidationResolver(validationSchema);
  const updateAssociate = useUpdateAssociate();

  const { handleSubmit, register, formState } =
    useForm<FullNameFormFieldValues>({
      defaultValues: {
        firstName: getDefaultFirstName(user),
        lastName: user?.lastName || '',
      },
      resolver,
    });

  const { errors } = formState;

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSubmit(async (formValues) => {
      const { firstName, lastName } = formValues;

      setIsLoading(true);
      try {
        await updateAssociate.mutateAsync({
          firstName,
          lastName,
        });
        await refetchUser();
        goToNextStep();
      } catch (e) {
        console.error(e);
        setError(true);
      }
      setIsLoading(false);
    })();
  };

  return (
    <Stack gap={4} sx={{ position: 'relative' }}>
      <ProfileSetupBackButton />
      <Stack gap={1} alignItems="center">
        <Typography variant="h5" textAlign="center">
          What should we call you?
        </Typography>
        <Typography textAlign="center">
          Your last name will not be shared with customers.
        </Typography>
      </Stack>
      {error && <ProfileSetupError title="name" />}
      <StyledForm onSubmit={handleFormSubmit} style={{ width: '100%' }}>
        <Stack gap={2}>
          <TextField
            slotProps={{
              input: register('firstName'),
            }}
            autoFocus
            label={`First name${
              errors?.firstName?.message ? ` - ${errors.firstName.message}` : ''
            }`}
            fullWidth
            error={Boolean(errors.firstName)}
            onFocus={(e) => e.target.select()}
          />
          <TextField
            slotProps={{
              input: register('lastName'),
            }}
            label={`Last name${
              errors?.lastName?.message ? ` - ${errors.lastName.message}` : ''
            }`}
            fullWidth
            error={Boolean(errors.lastName)}
          />
        </Stack>
        <ProfileSetupContinueButton type="submit" loading={isLoading}>
          Next
        </ProfileSetupContinueButton>
      </StyledForm>
    </Stack>
  );
};

export default EnterFullName;
